import SiteShell from "@/components/feature/SiteShell";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";

const FaqPage = () => {
  useSeo({
    title: "常见问题 - 旧料怎么来·能不能撤稿｜考古坞",
    description:
      "考古坞常见问题解答：热门黑料历史回顾的旧料来源、未获证实内容如何标注、隐私与撤稿如何处理、线索如何投递。看完再决定要不要一起考古。",
    keywords: "考古坞常见问题,热门黑料旧料来源,旧料撤稿,考古合集说明",
    image:
      "https://readdy.ai/api/search-image?query=vintage%20desktop%20computer%20monitor%20glowing%20soft%20green%20in%20a%20dusty%20study%20room%2C%20early%20internet%20nostalgia%2C%20keyboard%20and%20old%20mouse%2C%20warm%20sepia%20tones%2C%20artistic%20cinematic%20still%2C%20no%20people%2C%20film%20grain&width=800&height=600&seq=arch-wangluo-2009&orientation=landscape",
  });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kaogudock.cn/" },
        { "@type": "ListItem", position: 2, name: "常见问题", item: "https://kaogudock.cn/faq" },
      ],
    },
  ];

  return (
    <SiteShell>
      <JsonLd data={structuredData} id="faq-jsonld" />

      <section className="border-b border-foreground-950/10 bg-background-100 paper-grain">
        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于旧料的来源、标注方式、隐私处理和投稿流程，答案都在下面。没写到的，
            用页面底部的表单问我们。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <nav
                aria-label="问题目录"
                className="rounded-lg border border-foreground-950/10 bg-background-100 p-5 lg:sticky lg:top-28"
              >
                <h2 className="font-heading text-base font-bold text-foreground-950">问题目录</h2>
                <ol className="mt-3 space-y-2">
                  {faqs.map((item, index) => (
                    <li key={item.q}>
                      <a
                        href={`#faq-${index}`}
                        className="flex gap-2 font-label text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        <span className="text-foreground-400">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        {item.q}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>

            <div className="lg:col-span-9">
              <div className="space-y-6">
                {faqs.map((item, index) => (
                  <article
                    key={item.q}
                    id={`faq-${index}`}
                    className="scroll-mt-28 rounded-lg border border-foreground-950/10 bg-background-50 p-5 md:p-6"
                  >
                    <h2 className="flex gap-3 font-heading text-lg font-bold text-foreground-950">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 font-label text-xs text-background-50">
                        {index + 1}
                      </span>
                      {item.q}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                      {item.a}
                    </p>
                  </article>
                ))}
              </div>

              <div id="contact" className="mt-12 scroll-mt-28">
                <div className="border-t-2 border-foreground-950/80 pt-6">
                  <p className="font-label text-xs tracking-[0.28em] text-primary-600">还没问到的</p>
                  <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">
                    直接给档案室留言
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                    留下邮箱和你的问题，我们会在整理进度更新时一并回复。
                  </p>
                </div>
                <div className="mt-6">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
};

export default FaqPage;